import { ContinuousCode } from '@mm-shared/types/Status1';
import { KpiSettings } from '@mm-shared/types/Objective1';
import { calculateContinuousCode } from './kpiUtils';

/**
 * Get the Tailwind CSS background color class for a continuous status code
 *
 * Uses Tailwind's built-in color system defined in tailwind.config.js:
 * - Red spectrum (r1-r6): Poor performance requiring attention
 * - Yellow spectrum (y7-y12): Acceptable but improvable performance
 * - Green spectrum (g13-g22): Good to excellent performance
 *
 * Supports automatic theme switching (default, colorblind, grayscale)
 * via CSS custom properties in tailwind.config.js
 *
 * @param code - The continuous code (r1-r6, y7-y12, g13-g22)
 * @returns Tailwind CSS class string (e.g., 'bg-status-r1', 'bg-status-g22')
 *
 * @example
 * ```typescript
 * const bgClass = getBackgroundColorClass('r1');  // 'bg-status-r1'
 * const bgClass = getBackgroundColorClass('g22'); // 'bg-status-g22'
 * ```
 */
export function getBackgroundColorClass(code: ContinuousCode): string {
  return `bg-status-${code}`;
}

/**
 * Get the background color class for a metric value based on KPI settings
 * This is a convenience function that combines calculation and color lookup
 *
 * @param value - The metric value
 * @param kpiSettings - KPI settings defining thresholds
 * @returns Tailwind CSS background class representing the value's performance level
 *
 * @example
 * ```typescript
 * const settings = {
 *   numberType: '%',
 *   higherIsBetter: true,
 *   ranges: { bestGreen: 100, bestYellow: 90, worstYellow: 70, worstRed: 50 }
 * };
 * const bgClass = getColorClassForValue(85, settings); // 'bg-status-y10'
 * ```
 */
export function getColorClassForValue(value: number, kpiSettings: KpiSettings): string {
  const code = calculateContinuousCode(value, kpiSettings);
  return getBackgroundColorClass(code);
}
